<template>

  <Head :title="$t('admin.menu.users')" />

  <AdminLayout ref="adminLayout">
    <div class="flex flex-col h-[calc(100vh-90px)] justify-between">
      <div class="flex-1 overflow-auto">
        <div class="flex">
          <div class="flex-1 search-filters">
            <el-form :model="searchModel" label-position="top">
              <div class="grid grid-cols-6 gap-2">
                <div class="col-span-1">
                  <el-form-item prop="keyword" :label="$t('admin.user.search.quickSearch')">
                    <el-input v-model="searchModel.keyword" :placeholder="$t('admin.user.search.placeholder')" />
                  </el-form-item>
                </div>
                <div class="col-span-1">
                  <el-form-item prop="roles" :label="$t('admin.user.search.roles')">
                    <el-select v-model="searchModel.roles" :placeholder="$t('common.pleaseSelect')"
                      clearable class="w-full">
                      <!-- 需要添加选项 -->
                    </el-select>
                  </el-form-item>
                </div>
                <div class="col-span-1">
                  <el-form-item prop="createdTime" :label="$t('admin.user.search.registerTime')">
                    <el-date-picker v-model="searchModel.createdAt" type="daterange" range-separator="至"
                      start-placeholder="开始日期" end-placeholder="结束日期" class="w-full" />
                  </el-form-item>
                </div>
              </div>
            </el-form>
          </div>
          <div class="w-100 flex flex-col items-end space-y-2">
            <el-button type="primary" @click="eventHandler('search')">
              <el-icon class="mr-1"><Search /></el-icon>
              {{ $t('common.search') }}
            </el-button>
            <el-button @click="eventHandler('reset')">
              <el-icon class="mr-1"><RefreshRight /></el-icon>
              {{ $t('common.reset') }}
            </el-button>
          </div>
        </div>

        <el-divider class="!mt-[5px] !mb-[10px]" />
        
        <div class="flex justify-between mb-[10px]">
          <div>
            <el-button-group>
              <el-button :type="searchModel.group === 'all' ? 'primary' : 'default'"
                @click="groupChanged('all')">
                {{ $t('admin.user.group.all') }}
              </el-button>
              <el-button :type="searchModel.group === 'admin' ? 'primary' : 'default'"
                @click="groupChanged('admin')">
                {{ $t('admin.user.group.admin') }}
              </el-button>
              <el-button :type="searchModel.group === 'user' ? 'primary' : 'default'"
                @click="groupChanged('user')">
                {{ $t('admin.user.group.user') }}
              </el-button>
              <el-button :type="searchModel.group === 'guest' ? 'primary' : 'default'"
                @click="groupChanged('guest')">
                {{ $t('admin.user.group.guest') }}
              </el-button>
            </el-button-group>
          </div>
          <div>
            <el-button type="primary" @click="openDrawer('new')">
              <el-icon class="mr-1"><Plus /></el-icon>
              {{ $t('common.add') }}
            </el-button>
          </div>
        </div>

        <el-table :loading="loading" row-key="id" :data="rows" :border="false" class="w-full border-t">
          <el-table-column :label="$t('admin.user.fields.name')" min-width="200" fixed="left">
            <template #default="{ row }">
              <div class="cursor-pointer" @click="openDrawer('show', row.id)">
                <div class="text-gray-500 text-sm">ID: {{ row.id }}</div>
                <div class="flex items-center">
                  <el-avatar :size="32" class="min-w-[32px] mr-2">
                    <img v-if="row.avatarAttachment" alt="avatar" :src="row.avatarAttachment.url" />
                    <template v-else>{{ row.name && row.name.substr(0, 1) }}</template>
                  </el-avatar>
                  <div class="text-base font-normal text-primary">{{ row.name || $t('admin.user.fields.unnamed') }}</div>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column :label="$t('admin.user.fields.contact')" width="240">
            <template #default="{ row }">
              <div class="flex flex-col gap-1">
                <div v-if="row.phone">{{ $t('admin.user.fields.phone') }}：{{ row.phone }}</div>
                <div v-if="row.email">{{ $t('admin.user.fields.email') }}：{{ row.email }}</div>
              </div>
            </template>
          </el-table-column>
          <el-table-column :label="$t('admin.user.fields.roles')" prop="roles" width="130">
            <template #default="{ row }">
              <div class="flex flex-wrap gap-1">
                <el-tag v-for="(item, index) in row.roleTexts" :key="index" size="small">{{ item }}</el-tag>
              </div>
            </template>
          </el-table-column>
          <el-table-column :label="$t('common.operation')" width="120" fixed="right">
            <template #default="{ row }">
              <div class="flex justify-between">
                <el-button link type="primary" @click="openDrawer('edit', row.id)">{{ $t('common.edit') }}</el-button>
                <el-popconfirm :title="$t('common.confirmDelete')" type="warning"
                  @confirm="router.delete(routes.admin_sys_user(row.id))">
                  <template #reference>
                    <el-button link type="danger">{{ $t('common.delete') }}</el-button>
                  </template>
                </el-popconfirm>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
      
      <div class="mt-2 flex justify-end border-t pt-[20px]">
        <el-pagination
          v-model:current-page="pagination.current"
          v-model:page-size="pagination.pageSize"
          :page-sizes="[10, 20, 30, 40, 50]"
          layout="sizes, prev, pager, next, jumper, total"
          :total="pagination.total"
          @current-change="(current) => eventHandler('changePage', { page: current })"
          @size-change="(size) => eventHandler('changePageSize', { size })"
        />
      </div>
    </div>

    <template #drawer>
      <Drawer :options="options" ref="drawerRef" @close="adminLayout.closeDrawer()" />
    </template>
  </AdminLayout>
</template>

<script setup>
import AdminLayout from "@/layouts/Admin.vue"
import { Head } from "@inertiajs/vue3"
import Drawer from "./components/Drawer.vue"
import { Search, RefreshRight, Plus } from '@element-plus/icons-vue'

const props = defineProps({
  pagy: Object,
  rows: Array,
  options: Object,
  flash: Object
})

const { t } = useI18n()
const { triggerFlash } = useFlash()
const adminLayout = ref(null)
const drawerRef = ref(null)

const { loading, pagination, updatePagination, searchModel, queryData, eventHandler } = useSearch(routes.admin_sys_users(), {
  keyword: "",
  type: "",
  status: "",
  createdAt: [],
  group: "all",
  page: 1,
  size: 20,
})

updatePagination(props.pagy)

const groupChanged = (group) => {
  searchModel.value.group = group
  queryData()
}

const openDrawer = async (drawerMode, id = 0) => {
  adminLayout.value.openDrawer()
  await nextTick()
  if (drawerRef.value) {
    await drawerRef.value.updateDrawerMode(drawerMode, id)
    adminLayout.value.stopDrawerLoading()
  }
}

watchEffect(() => { if (props.flash) { triggerFlash(props.flash) } })
</script>
